/**
 * Filter catalog profiles by User Type (pure — no I/O).
 * Keep in sync with backend/src/domain/filters/filterProfiles.js.
 */

export const PRIMARY_FILTER_FIELD = 'role';

export const FILTER_USER_TYPES = Object.freeze([
  'student',
  'admin',
  'trainer',
  'recruiter',
  'staff',
]);

const ALL_FILTER_CATEGORIES = Object.freeze([
  'general',
  'personal',
  'enrollment',
  'student',
  'academic',
  'placement',
  'interview',
  'training',
  'counselling',
  'legacy',
]);

const STUDENT_CATEGORIES = Object.freeze([...ALL_FILTER_CATEGORIES]);
const STAFF_IDENTITY_CATEGORIES = Object.freeze(['general', 'personal']);

const STUDENT_ADDITIONAL_CATEGORIES = Object.freeze([
  'academic',
  'counselling',
  'placement',
  'interview',
  'training',
  'legacy',
]);

export const FILTER_CATEGORY_LABELS = Object.freeze({
  personal: 'Personal Information',
  enrollment: 'Enrollment & Course',
  student: 'Domain',
  academic: 'Academic Qualifications',
  placement: 'Placement & Opportunities',
  interview: 'Interview Readiness & Mock Interviews',
  training: 'Training Concerns',
  counselling: 'Counselling Details',
  general: 'General',
  legacy: 'More fields',
});

export const FILTER_PROFILES = Object.freeze({
  student: Object.freeze({
    userType: 'student',
    categories: STUDENT_CATEGORIES,
    additionalCategories: STUDENT_ADDITIONAL_CATEGORIES,
    defaultOpenCategories: Object.freeze([]),
  }),
  admin: Object.freeze({
    userType: 'admin',
    categories: STAFF_IDENTITY_CATEGORIES,
    additionalCategories: Object.freeze([]),
    defaultOpenCategories: Object.freeze([]),
  }),
  staff: Object.freeze({
    userType: 'staff',
    categories: STAFF_IDENTITY_CATEGORIES,
    additionalCategories: Object.freeze([]),
    defaultOpenCategories: Object.freeze([]),
  }),
  trainer: Object.freeze({
    userType: 'trainer',
    categories: Object.freeze(['general', 'personal', 'enrollment', 'student']),
    additionalCategories: Object.freeze(['enrollment', 'student']),
    defaultOpenCategories: Object.freeze([]),
  }),
  recruiter: Object.freeze({
    userType: 'recruiter',
    categories: Object.freeze(['general', 'personal', 'placement']),
    additionalCategories: Object.freeze(['placement']),
    defaultOpenCategories: Object.freeze([]),
  }),
});

export function normalizeFilterUserType(role) {
  const key = String(role || '')
    .trim()
    .toLowerCase();
  if (FILTER_USER_TYPES.includes(key)) return key;
  return 'student';
}

export function filterProfileForUserType(role) {
  return FILTER_PROFILES[normalizeFilterUserType(role)];
}

export function isFilterCategoryVisible(role, category) {
  const cat = String(category || '').trim();
  return filterProfileForUserType(role).categories.includes(cat);
}

export function filterFieldsForUserType(fields, role) {
  const allowed = new Set(filterProfileForUserType(role).categories);
  return (fields || []).filter(
    (field) => field?.name === PRIMARY_FILTER_FIELD || allowed.has(field?.category)
  );
}

export function defaultExpandedFilterSections(role) {
  const expanded = {};
  for (const category of filterProfileForUserType(role).categories) {
    expanded[category] = false;
  }
  return expanded;
}
